import { AggregatePaginateResult, BILLING_DOMAIN, BillingCardInfo, BillingInvoice, BillingInvoiceConsumption, BillingSettings } from "@finway-group/shared/lib/models"
import { useQuery } from "@tanstack/react-query"
import { SortOrder } from "antd/lib/table/interface"
import { queryClient } from "~Shared/config/reactQueryConfig"
import { CacheEnum } from "~Shared/queries/consts"
import { convertSortingToString } from "~Shared/queries/query.utils"
import { queryGet } from "~Shared/utils/reactQuery.utils"

export const invalidateBillingSettings = async () => {
    await queryClient.invalidateQueries({
        queryKey: [CacheEnum.BILLING_SETTINGS],
    })
}

export const useBillingSettings = ({ isEnabled = true }: { isEnabled?: boolean }) =>
    useQuery({
        queryKey: [CacheEnum.BILLING_SETTINGS],
        refetchOnWindowFocus: false,
        enabled: isEnabled,
        queryFn: ({ signal }) => queryGet<BillingSettings>(`${BILLING_DOMAIN}`, { signal }),
    })

export const updateBillingSettings = (updatedBillingSettings: BillingSettings) => {
    queryClient.setQueryData([CacheEnum.BILLING_SETTINGS], updatedBillingSettings)
}

export const useBillingInvoiceConsumption = ({ isEnabled = true }: { isEnabled?: boolean }) =>
    useQuery({
        queryKey: [CacheEnum.BILLING_INVOICE_CONSUMPTION],
        refetchOnWindowFocus: false,
        enabled: isEnabled,
        queryFn: ({ signal }) => queryGet<BillingInvoiceConsumption>(`${BILLING_DOMAIN}/invoice-consumption`, { signal }),
    })

export const useBillingCardInfo = ({ isEnabled = true }: { isEnabled?: boolean }) =>
    useQuery({
        queryKey: [CacheEnum.BILLING_CARDS_COUNT],
        refetchOnWindowFocus: false,
        enabled: isEnabled,
        queryFn: ({ signal }) => queryGet<BillingCardInfo>(`${BILLING_DOMAIN}/card-info`, { signal }),
    })

export const BILLING_INVOICES_DEFAULT_PAGE_SIZE = 5

// TODO Unify w/ other QueryOptions types like the one used for vendors
export type QueryOptions<T> = {
    sorting?: {
        order: SortOrder
        field: keyof T
    }
    page: number
    limit: number
    isEnabled: boolean
}

export const useBillingInvoices = ({ sorting, page = 1, limit = BILLING_INVOICES_DEFAULT_PAGE_SIZE, isEnabled = false }: QueryOptions<BillingInvoice>) => {
    const sortingCriteria = convertSortingToString(sorting)
    return useQuery({
        queryKey: [CacheEnum.BILLING_INVOICES, limit, page, sortingCriteria],
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        enabled: isEnabled,
        queryFn: ({ signal }) =>
            queryGet<AggregatePaginateResult<BillingInvoice>>(`${BILLING_DOMAIN}/invoices?page=${page}&limit=${limit}${sortingCriteria}`, {
                signal,
            }),
        getNextPageParam: (lastPage) => (lastPage.hasNextPage ? (lastPage.page ?? 0) + 1 : undefined),
    })
}
